import { Fragment, useMemo, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ChevronDown,
  ChevronUp,
  Download,
  ExternalLink,
  Loader2,
  MessageSquare,
  ThumbsDown,
  ThumbsUp,
} from 'lucide-react';
import { apiFetch } from '../../lib/apiFetch';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type FeedbackStatus = 'new' | 'triaged' | 'in_progress' | 'done' | 'no_action';
type FeedbackSurface = 'search' | 'upload';
type FeedbackRating = 'up' | 'down';

export interface FeedbackFieldItem {
  key: string;
  label: string;
  verdict: 'good' | 'bad';
  displayedValue?: string | null;
  comment?: string | null;
  meta?: Record<string, unknown>;
}

export interface FeedbackReview {
  theme: string | null;
  response: string | null;
  actionRef: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
}

export interface FeedbackItem {
  id: string;
  createdAt: string | null;
  modifiedAt: string | null;
  surface: FeedbackSurface;
  rating: FeedbackRating;
  user: string | null;
  route: string | null;
  context: {
    query?: string | null;
    fileName?: string | null;
    title?: string | null;
    recordId?: string | null;
  };
  improvement: string | null;
  expected: { description: string | null; recordId: string | null } | null;
  fields: FeedbackFieldItem[];
  hasComment: boolean;
  status: FeedbackStatus;
  review: FeedbackReview;
  suggestedTheme: string | null;
  _links?: { self?: { href: string } };
}

interface RatingCounts {
  votes: number;
  up: number;
  down: number;
  upRate: number | null;
  withComment: number;
  commentRate: number | null;
}

interface UsageCounts extends RatingCounts {
  eligible: number;
  responseRate: number | null;
}

interface WeeklyPoint {
  weekStart: string;
  searches: number;
  uploads: number;
  votes: number;
  up: number;
  down: number;
  comments: number;
  responseRate: number | null;
  upRate: number | null;
}

export interface FeedbackStatistics {
  dateFrom: string;
  dateTo: string;
  totals: UsageCounts;
  commentRateByRating: Record<FeedbackRating, number | null>;
  bySurface: Record<FeedbackSurface, UsageCounts>;
  voters: {
    distinct: number;
    activeUsers: number;
    topVoterCount: number;
    topVoterShare: number | null;
  };
  byStatus: Record<FeedbackStatus, number>;
  byTheme: Record<string, number>;
  topBadFields: { label: string; count: number }[];
  weekly: WeeklyPoint[];
}

// ---------------------------------------------------------------------------
// Labels — keep in sync with gcp-ara-api src/ops/feedback.py and feedback_export.py
// ---------------------------------------------------------------------------

const STATUS_OPTIONS: { value: FeedbackStatus; label: string }[] = [
  { value: 'new', label: 'New' },
  { value: 'triaged', label: 'Triaged' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'done', label: 'Done' },
  { value: 'no_action', label: 'No action planned' },
];
const STATUS_LABELS = Object.fromEntries(STATUS_OPTIONS.map((o) => [o.value, o.label])) as Record<
  FeedbackStatus,
  string
>;
const CLOSED_STATUSES: FeedbackStatus[] = ['done', 'no_action'];
const OPEN_STATUSES: FeedbackStatus[] = ['new', 'triaged', 'in_progress'];

const THEME_OPTIONS: { value: string; label: string }[] = [
  { value: 'search_relevance', label: 'Search relevance' },
  { value: 'entity_recognition', label: 'Entity recognition' },
  { value: 'filters', label: 'Filters' },
  { value: 'title_summary', label: 'Title and summary' },
  { value: 'classification', label: 'Classification' },
  { value: 'address', label: 'Address' },
  { value: 'sensitivity', label: 'Sensitivity (personal, security, Māori data)' },
  { value: 'previous_version', label: 'Previous version' },
  { value: 'performance', label: 'Performance' },
  { value: 'other', label: 'Other' },
];
const THEME_LABELS: Record<string, string> = Object.fromEntries(
  THEME_OPTIONS.map((o) => [o.value, o.label]),
);

const SURFACE_LABELS: Record<FeedbackSurface, string> = { search: 'Search', upload: 'Upload' };

const STATUS_BADGE: Record<FeedbackStatus, string> = {
  new: 'bg-sky-100 text-sky-800',
  triaged: 'bg-violet-100 text-violet-800',
  in_progress: 'bg-amber-100 text-amber-800',
  done: 'bg-emerald-100 text-emerald-800',
  no_action: 'bg-gray-100 text-gray-700',
};

const UP_COLOR = '#059669';
const DOWN_COLOR = '#DC2626';
const RATE_COLOR = '#6366F1';

const MAX_TEXT_LENGTH = 4000;
const MAX_ACTION_REF_LENGTH = 500;
const PREVIEW_LENGTH = 120;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return `${(value * 100).toFixed(value > 0 && value < 0.1 ? 1 : 0)}%`;
}

function formatDateTime(value: string | null): string {
  if (!value) return 'Unknown date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function formatWeek(value: string): string {
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function truncate(text: string, length = PREVIEW_LENGTH): string {
  return text.length > length ? `${text.slice(0, length)}…` : text;
}

function commentPreview(item: FeedbackItem): string | null {
  return (
    item.improvement ||
    item.fields.find((field) => field.comment)?.comment ||
    item.expected?.description ||
    null
  );
}

function contextLabel(item: FeedbackItem): string {
  if (item.surface === 'search') return item.context.query ? `“${item.context.query}”` : 'Search';
  return item.context.title || item.context.fileName || 'Upload';
}

/** The stored route is client-supplied, so only same-site paths become links. */
function sameSitePath(route: string | null): string | null {
  return route && route.startsWith('/') && !route.startsWith('//') ? route : null;
}

function recordHref(recordId: string): string {
  return `/records/${encodeURIComponent(recordId)}`;
}

function themeLabel(theme: string | null | undefined): string | null {
  return theme ? (THEME_LABELS[theme] ?? theme) : null;
}

async function readError(response: Response): Promise<string> {
  try {
    const body = await response.json();
    if (typeof body?.error === 'string') return body.error;
  } catch {
    /* fall through to the generic message */
  }
  return `Request failed (${response.status})`;
}

// ---------------------------------------------------------------------------
// Summary
// ---------------------------------------------------------------------------

function StatTile({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50/60 px-4 py-3">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="mt-1 text-xl font-semibold text-gray-900">{value}</p>
      {detail && <p className="mt-0.5 text-xs text-gray-500">{detail}</p>}
    </div>
  );
}

function FeedbackSummary({ statistics }: { statistics: FeedbackStatistics }) {
  const { totals, bySurface, voters, commentRateByRating, byStatus } = statistics;
  const open = OPEN_STATUSES.reduce((sum, status) => sum + (byStatus[status] ?? 0), 0);
  const chartData = statistics.weekly.map((week) => ({
    ...week,
    label: formatWeek(week.weekStart),
  }));

  return (
    <div className="space-y-4 border-b border-gray-100 p-5">
      <p className="text-xs text-gray-500">
        Votes are voluntary. Read the up rate alongside the response rate and the number of distinct
        voters.
      </p>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
        <StatTile label="Completed searches" value={String(bySurface.search.eligible)} />
        <StatTile label="Uploads reviewed" value={String(bySurface.upload.eligible)} />
        <StatTile
          label="Response rate"
          value={formatPercent(totals.responseRate)}
          detail={`Search ${formatPercent(bySurface.search.responseRate)} · Upload ${formatPercent(
            bySurface.upload.responseRate,
          )}`}
        />
        <StatTile
          label="Up rate"
          value={formatPercent(totals.upRate)}
          detail={`${totals.up} up · ${totals.down} down`}
        />
        <StatTile
          label="Distinct voters"
          value={String(voters.distinct)}
          detail={`of ${voters.activeUsers} active users · top ${voters.topVoterCount} cast ${formatPercent(
            voters.topVoterShare,
          )}`}
        />
        <StatTile
          label="With a comment"
          value={formatPercent(totals.commentRate)}
          detail={`${formatPercent(commentRateByRating.up)} of up · ${formatPercent(
            commentRateByRating.down,
          )} of down`}
        />
        <StatTile
          label="Review status"
          value={`${open} open`}
          detail={`${byStatus.done ?? 0} done · ${byStatus.no_action ?? 0} no action`}
        />
      </div>

      {chartData.length > 0 && (
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: '#9CA3AF' }}
              tickLine={false}
              axisLine={{ stroke: '#E5E7EB' }}
            />
            <YAxis
              yAxisId="votes"
              allowDecimals={false}
              tick={{ fontSize: 11, fill: '#9CA3AF' }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              yAxisId="rate"
              orientation="right"
              domain={[0, 'auto']}
              tickFormatter={(value: number) => formatPercent(value)}
              tick={{ fontSize: 11, fill: '#9CA3AF' }}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip
              formatter={(value, name) =>
                name === 'Response rate' ? formatPercent(value as number | null) : value
              }
              labelFormatter={(label) => `Week of ${label}`}
            />
            <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '12px' }} />
            <Bar yAxisId="votes" dataKey="up" name="Thumbs up" stackId="votes" fill={UP_COLOR} />
            <Bar
              yAxisId="votes"
              dataKey="down"
              name="Thumbs down"
              stackId="votes"
              fill={DOWN_COLOR}
              radius={[4, 4, 0, 0]}
            />
            <Line
              yAxisId="rate"
              type="monotone"
              dataKey="responseRate"
              name="Response rate"
              stroke={RATE_COLOR}
              strokeWidth={2}
              dot={{ r: 3 }}
              connectNulls
            />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail and review
// ---------------------------------------------------------------------------

function FeedbackReviewForm({
  item,
  onSaved,
}: {
  item: FeedbackItem;
  onSaved: () => Promise<void> | void;
}) {
  const [status, setStatus] = useState<FeedbackStatus>(item.status);
  const [theme, setTheme] = useState(item.review.theme ?? item.suggestedTheme ?? '');
  const [response, setResponse] = useState(item.review.response ?? '');
  const [actionRef, setActionRef] = useState(item.review.actionRef ?? '');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selfHref = item._links?.self?.href;
  const needsResponse = CLOSED_STATUSES.includes(status) && !response.trim();

  const save = async () => {
    if (!selfHref || pending || needsResponse) return;
    setPending(true);
    setError(null);
    try {
      const res = await apiFetch(selfHref, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status,
          theme: theme || null,
          response: response || null,
          action_ref: actionRef || null,
        }),
      });
      if (!res.ok) throw new Error(await readError(res));
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save review');
    } finally {
      setPending(false);
    }
  };

  const inputClass =
    'mt-1 w-full rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500';

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-4">
      <p className="text-sm font-semibold text-gray-900">Project team review</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="text-xs font-medium text-gray-600">Status</span>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as FeedbackStatus)}
            className={inputClass}
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-600">Theme</span>
          <select value={theme} onChange={(e) => setTheme(e.target.value)} className={inputClass}>
            <option value="">Not set</option>
            {THEME_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
                {!item.review.theme && option.value === item.suggestedTheme ? ' (suggested)' : ''}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="block">
        <span className="text-xs font-medium text-gray-600">
          Response — what changed, what is outstanding, or why no action is planned
        </span>
        <textarea
          rows={3}
          maxLength={MAX_TEXT_LENGTH}
          value={response}
          onChange={(e) => setResponse(e.target.value)}
          className={`${inputClass} resize-y`}
        />
      </label>
      <label className="block">
        <span className="text-xs font-medium text-gray-600">Action reference (ticket or PR link)</span>
        <input
          type="text"
          maxLength={MAX_ACTION_REF_LENGTH}
          value={actionRef}
          onChange={(e) => setActionRef(e.target.value)}
          className={inputClass}
        />
      </label>
      {needsResponse && (
        <p className="text-xs text-amber-700">A response is required to close this item.</p>
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-gray-400">
          {item.review.reviewedBy
            ? `Last reviewed by ${item.review.reviewedBy} · ${formatDateTime(item.review.reviewedAt)}`
            : 'Not reviewed yet'}
        </p>
        <button
          type="button"
          onClick={() => void save()}
          disabled={!selfHref || pending || needsResponse}
          className="flex items-center gap-1.5 rounded-lg bg-emerald-700 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {pending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Save review
        </button>
      </div>
    </div>
  );
}

function FeedbackDetail({
  item,
  onReviewed,
}: {
  item: FeedbackItem;
  onReviewed: () => Promise<void> | void;
}) {
  const linkClass =
    'inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-900 hover:underline';
  const pageHref = sameSitePath(item.route);

  return (
    <div className="grid grid-cols-1 gap-4 bg-gray-50/60 px-5 py-4 lg:grid-cols-2">
      <div className="space-y-3 text-sm">
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
          <span>Submitted by {item.user || 'Unknown'}</span>
          {item.modifiedAt && <span>Edited {formatDateTime(item.modifiedAt)}</span>}
        </div>
        <div className="flex flex-wrap gap-3">
          {pageHref && (
            <a href={pageHref} target="_blank" rel="noopener noreferrer" className={linkClass}>
              Open page <ExternalLink className="h-3 w-3" />
            </a>
          )}
          {item.context.recordId && (
            <a
              href={recordHref(item.context.recordId)}
              target="_blank"
              rel="noopener noreferrer"
              className={linkClass}
            >
              Open record <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
        {item.surface === 'upload' && item.context.fileName && (
          <p className="text-xs text-gray-600">
            <span className="font-medium text-gray-700">File:</span> {item.context.fileName}
          </p>
        )}
        <div>
          <p className="text-xs font-medium text-gray-600">What could be improved</p>
          <p className="mt-0.5 whitespace-pre-wrap break-words text-gray-800">
            {item.improvement || <span className="text-gray-400">No comment</span>}
          </p>
        </div>
        {item.expected && (item.expected.recordId || item.expected.description) && (
          <div>
            <p className="text-xs font-medium text-gray-600">Expected</p>
            {item.expected.description && (
              <p className="mt-0.5 whitespace-pre-wrap text-gray-800">{item.expected.description}</p>
            )}
            {item.expected.recordId && (
              <a
                href={recordHref(item.expected.recordId)}
                target="_blank"
                rel="noopener noreferrer"
                className={linkClass}
              >
                Expected record <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        )}
        {item.fields.length > 0 && (
          <div>
            <p className="text-xs font-medium text-gray-600">Fields singled out</p>
            <ul className="mt-1 space-y-2">
              {item.fields.map((field) => (
                <li key={field.key} className="rounded-md border border-gray-100 bg-white p-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-xs font-medium text-gray-700">{field.label}</p>
                    {field.verdict === 'good' ? (
                      <ThumbsUp className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                    ) : (
                      <ThumbsDown className="h-3.5 w-3.5 shrink-0 text-red-600" />
                    )}
                  </div>
                  {field.displayedValue && (
                    <p className="mt-0.5 break-words text-xs text-gray-500">{field.displayedValue}</p>
                  )}
                  {field.comment && (
                    <p className="mt-1 whitespace-pre-wrap break-words text-xs text-gray-800">
                      {field.comment}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <FeedbackReviewForm
        key={`${item.id}:${item.review.reviewedAt ?? ''}`}
        item={item}
        onSaved={onReviewed}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pane
// ---------------------------------------------------------------------------

interface Filters {
  rating: '' | FeedbackRating;
  status: '' | 'open' | FeedbackStatus;
  surface: '' | FeedbackSurface;
  hasComment: boolean;
}

const DEFAULT_FILTERS: Filters = { rating: '', status: '', surface: '', hasComment: false };

export default function FeedbackPane({
  items,
  statistics,
  exportHref,
  onReviewed,
}: {
  items: FeedbackItem[];
  statistics: FeedbackStatistics | null;
  exportHref?: string;
  onReviewed: () => Promise<void> | void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [openId, setOpenId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const visibleItems = useMemo(
    () =>
      items.filter((item) => {
        if (filters.rating && item.rating !== filters.rating) return false;
        if (filters.surface && item.surface !== filters.surface) return false;
        if (filters.hasComment && !item.hasComment) return false;
        if (filters.status === 'open') return OPEN_STATUSES.includes(item.status);
        if (filters.status && item.status !== filters.status) return false;
        return true;
      }),
    [items, filters],
  );

  const up = statistics?.totals.up ?? items.filter((item) => item.rating === 'up').length;
  const down = statistics?.totals.down ?? items.filter((item) => item.rating === 'down').length;
  const open = items.filter((item) => OPEN_STATUSES.includes(item.status)).length;

  const exportWorkbook = async () => {
    if (!exportHref || !statistics || exporting) return;
    setExporting(true);
    setExportError(null);
    try {
      const params = new URLSearchParams({
        date_from: statistics.dateFrom,
        date_to: statistics.dateTo,
      });
      const response = await apiFetch(`${exportHref}?${params.toString()}`);
      if (!response.ok) throw new Error(await readError(response));
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = `ara-feedback-${statistics.dateFrom}-to-${statistics.dateTo}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Failed to export feedback');
    } finally {
      setExporting(false);
    }
  };

  const selectClass =
    'rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500';

  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div
        className={`flex flex-wrap items-center justify-between gap-3 px-5 py-4 ${
          expanded ? 'border-b border-gray-100' : ''
        }`}
      >
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
          className="flex items-center gap-2 text-left"
        >
          <MessageSquare className="h-5 w-5 text-emerald-600" />
          <h2 className="text-lg font-semibold text-gray-900">User Feedback</h2>
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          )}
        </button>
        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <ThumbsUp className="h-3.5 w-3.5 text-emerald-600" /> {up}
          </span>
          <span className="flex items-center gap-1">
            <ThumbsDown className="h-3.5 w-3.5 text-red-600" /> {down}
          </span>
          <span className="rounded-full bg-amber-100 px-2.5 py-1 font-medium text-amber-800">
            {open} open
          </span>
          <span className="rounded-full bg-emerald-800 px-2.5 py-1 font-medium text-white">
            {items.length} items
          </span>
          {exportHref && (
            <button
              type="button"
              onClick={() => void exportWorkbook()}
              disabled={!statistics || exporting}
              className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {exporting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Download className="h-3.5 w-3.5" />
              )}
              Export to Excel
            </button>
          )}
        </div>
        {exportError && <p className="w-full text-right text-xs text-red-600">{exportError}</p>}
      </div>

      {expanded && (
        <>
          {statistics && <FeedbackSummary statistics={statistics} />}

          <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-5 py-3">
            <select
              aria-label="Filter by rating"
              value={filters.rating}
              onChange={(e) => setFilters((f) => ({ ...f, rating: e.target.value as Filters['rating'] }))}
              className={selectClass}
            >
              <option value="">All ratings</option>
              <option value="up">Thumbs up</option>
              <option value="down">Thumbs down</option>
            </select>
            <select
              aria-label="Filter by status"
              value={filters.status}
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value as Filters['status'] }))}
              className={selectClass}
            >
              <option value="">All statuses</option>
              <option value="open">Open</option>
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <select
              aria-label="Filter by surface"
              value={filters.surface}
              onChange={(e) =>
                setFilters((f) => ({ ...f, surface: e.target.value as Filters['surface'] }))
              }
              className={selectClass}
            >
              <option value="">Search and upload</option>
              <option value="search">Search</option>
              <option value="upload">Upload</option>
            </select>
            <label className="flex items-center gap-1.5 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={filters.hasComment}
                onChange={(e) => setFilters((f) => ({ ...f, hasComment: e.target.checked }))}
                className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              />
              Has comment
            </label>
            <span className="ml-auto text-xs text-gray-400">
              Showing {visibleItems.length} of {items.length}
            </span>
          </div>

          {visibleItems.length === 0 ? (
            <div className="p-5">
              <p className="rounded-lg border border-dashed border-gray-200 py-12 text-center text-sm text-gray-400">
                {items.length === 0
                  ? 'No feedback in this date range'
                  : 'No feedback matches these filters'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100 text-sm">
                <thead className="bg-gray-50/80 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-5 py-3">Submitted</th>
                    <th className="px-5 py-3">Rating</th>
                    <th className="px-5 py-3">Context</th>
                    <th className="px-5 py-3">Comment</th>
                    <th className="px-5 py-3">Theme</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3">Response</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {visibleItems.map((item) => {
                    const isOpen = openId === item.id;
                    const comment = commentPreview(item);
                    const reviewedTheme = themeLabel(item.review.theme);
                    const suggestedTheme = themeLabel(item.suggestedTheme);
                    return (
                      <Fragment key={item.id}>
                        <tr
                          onClick={() => setOpenId(isOpen ? null : item.id)}
                          aria-expanded={isOpen}
                          className={`cursor-pointer align-top transition-colors hover:bg-gray-50 ${
                            isOpen ? 'bg-gray-50' : 'bg-white'
                          }`}
                        >
                          <td className="whitespace-nowrap px-5 py-3 text-gray-600">
                            <div>{formatDateTime(item.createdAt)}</div>
                            <div className="text-xs text-gray-400">{SURFACE_LABELS[item.surface]}</div>
                          </td>
                          <td className="px-5 py-3">
                            {item.rating === 'up' ? (
                              <ThumbsUp className="h-4 w-4 text-emerald-600" aria-label="Thumbs up" />
                            ) : (
                              <ThumbsDown className="h-4 w-4 text-red-600" aria-label="Thumbs down" />
                            )}
                          </td>
                          <td className="max-w-[16rem] break-words px-5 py-3 text-gray-700">
                            {truncate(contextLabel(item), 80)}
                          </td>
                          <td className="max-w-[20rem] break-words px-5 py-3 text-gray-700">
                            {comment ? truncate(comment) : <span className="text-gray-400">—</span>}
                            {item.fields.length > 0 && (
                              <div className="mt-0.5 text-xs text-gray-400">
                                {item.fields.length} field{item.fields.length === 1 ? '' : 's'} singled out
                              </div>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-5 py-3 text-gray-700">
                            {reviewedTheme ??
                              (suggestedTheme ? (
                                <span className="italic text-gray-400">{suggestedTheme}?</span>
                              ) : (
                                <span className="text-gray-400">—</span>
                              ))}
                          </td>
                          <td className="whitespace-nowrap px-5 py-3">
                            <span
                              className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                                STATUS_BADGE[item.status] ?? STATUS_BADGE.new
                              }`}
                            >
                              {STATUS_LABELS[item.status] ?? item.status}
                            </span>
                          </td>
                          <td className="max-w-[20rem] break-words px-5 py-3 text-gray-700">
                            {item.review.response ? (
                              truncate(item.review.response)
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr>
                            <td colSpan={7} className="p-0">
                              <FeedbackDetail item={item} onReviewed={onReviewed} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
